import { useEffect, useState, useMemo, useRef } from "react";
import {
  Grid,
  Stack,
  useToast,
  Heading,
  Box,
  Flex,
  Button,
  Text,
  Input,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  IconButton,
  GridItem,
  Icon,
  Select,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Radio,
  RadioGroup,
  VStack,
  Skeleton,
  useOutsideClick,
} from "@chakra-ui/react";
import { Loading } from "../../../components/Loading";
import useDeleteToast from "../../../components/DeleteToast";
import { DeleteAlert } from "../../../components/DeleteAlert";
import { FiMoreVertical, FiSearch } from "react-icons/fi";
import { CgAddR } from "react-icons/cg";
import { Unauthorized } from "../../../components/Unauthorized";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Link } from "react-router-dom";
import {
  getFleetsList,
  deleteFleet,
} from "../../../actions/inventory/fleet.action";
import { FaSackDollar } from "react-icons/fa6";
import { FaHandHoldingUsd } from "react-icons/fa";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import { ImFilter } from "react-icons/im";
import { TbArrowsSort } from "react-icons/tb";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { useNavigate } from "react-router-dom";

const Fleets = () => {
  const toast = useToast();
  const debounceTimer = useRef(null);
  const filterRef = useRef(null);
  const showDeleteToast = useDeleteToast();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [globalStats, setGlobalStats] = useState({ total: 0, onDuty: 0, idle: 0 });
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFleets, setTotalFleets] = useState(0);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Applied filter state — triggers API call
  const [filterOperationalStatus, setFilterOperationalStatus] = useState("");
  const [filterAdministrativeStatus, setFilterAdministrativeStatus] = useState("");

  // Temp filter state — only lives inside the dropdown until Apply
  const [tempOperationalStatus, setTempOperationalStatus] = useState("");
  const [tempAdministrativeStatus, setTempAdministrativeStatus] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useOutsideClick({
    ref: filterRef,
    handler: () => setIsFilterOpen(false),
  });

  const standardOptions = [10, 20, 50];

  const perPageOptions = useMemo(() => {
    return Array.from(new Set([...standardOptions, itemsPerPage])).sort((a, b) => a - b);
  }, [itemsPerPage]);

  const legacyOperationalStatusMap = {
    0: "in_review",
    1: "active",
    2: "inactive",
    3: "blacklisted",
  };
  const normalizeOperationalStatus = (value) =>
    legacyOperationalStatusMap[value] || value || "in_review";
  const operationalStatusMap = {
    in_review: "In Review",
    active: "Active",
    inactive: "Inactive",
    blacklisted: "Blacklisted",
  };
  const operationalStatusColorMap = {
    in_review: "gray",
    active: "green",
    inactive: "red",
    blacklisted: "black",
  };
  const administrativeStatusMap = { 0: "Available", 1: "On Duty", 2: "Under Maintenance", 3: "Out Of Service" };
  const administrativeStatusColorMap = { 0: "blue", 1: "green", 2: "orange", 3: "red" };

  const loadData = async () => {
    setFetching(true);
    try {
      const { data, totalPages, totalFleets, globalStats } =
        await getFleetsList(
          currentPage,
          itemsPerPage,
          query,
          filterOperationalStatus,
          filterAdministrativeStatus
        );

      const formattedData = data.map((item) => ({
        id: item._id,
        carNumber: item?.documents?.registrationCertificate?.documentNumber || "-",
        model: item?.model || "-",
        manufacturer: item?.manufacturer || "-",
        category: item?.vehicle?.segmentName || item?.vehicleType?.segmentName || item?.vehicleSegment?.segmentName || "-",
        operationalStatus: normalizeOperationalStatus(item?.status?.operationalStatus),
        administrativeStatus: item?.status?.administrativeStatus ?? 0,
      }));

      setData(formattedData);
      setTotalPages(totalPages ?? 1);
      setTotalFleets(totalFleets ?? 0);
      setGlobalStats(globalStats);
    } catch (error) {
      setError(error.message);
      toast({
        title: "Failed to fetch data!",
        description: error.message,
        status: "error",
        duration: 9000,
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  };

  // Client-side sort only (no filtering — that's server-side now)
  const sortedData = useMemo(() => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortField, sortOrder]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  // Re-fetch whenever page, limit, query, or filters change
  useEffect(() => {
    loadData();
  }, [currentPage, itemsPerPage, query, filterOperationalStatus, filterAdministrativeStatus]);

  const clearSearch = () => {
    setSearchInput("");
    setQuery("");
    setCurrentPage(1);
  };

  const handleFilterOpen = () => {
    setTempOperationalStatus(filterOperationalStatus);
    setTempAdministrativeStatus(filterAdministrativeStatus);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setFilterOperationalStatus(tempOperationalStatus);
    setFilterAdministrativeStatus(tempAdministrativeStatus);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setTempOperationalStatus("");
    setTempAdministrativeStatus("");
    setFilterOperationalStatus("");
    setFilterAdministrativeStatus("");
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const applySort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  if (loading) return <Loading />;
  if (error === "Unauthorized user") return <Unauthorized />;

  const handleDelete = async (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteFleet(deleteConfirm.id);
      showDeleteToast("Fleet deleted successfully");
      loadData();
    } catch (err) {
      toast({
        title: "Failed to delete fleet",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const getPagesToShow = () => {
    if (totalPages === 0) return [];
    let startPage = 1;
    let endPage = totalPages;
    if (totalPages > 5) {
      if (currentPage <= 3) { startPage = 1; endPage = 5; }
      else if (currentPage >= totalPages - 2) { startPage = totalPages - 4; endPage = totalPages; }
      else { startPage = currentPage - 2; endPage = currentPage + 2; }
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <>
      <DeleteAlert
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onDelete={onDeleteConfirm}
        label="Fleet"
        itemName="Fleet"
      />
      <Stack w={"100%"} justifyContent={"center"} spacing={10} p={6}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <BreadCrumbMaker
            items={[
              { link: "", title: "Fleet Management" },
              { link: "/fleet-management/vehicles", title: "Vehicle", on: true },
            ]}
          />
          <Button
            to="/fleet-management/vehicles/add"
            as={Link}
            colorScheme="blue"
            size="sm"
            borderRadius={5}
            px={10}
            fontWeight={0}
            py={5}
          >
            <Flex gap={1} alignItems={"center"}>
              <CgAddR /> <Text>Add Vehicle</Text>
            </Flex>
          </Button>
        </Flex>

        {/* Summary Cards */}
        <Box>
          <Grid
            gridGap={6}
            templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, minmax(250px, 1fr))" }}
          >
            <GridItem key="onDutyFleet">
              <Box bg="white" p={4} rounded="md" display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" h="120px" gap={2} borderRadius="3xl">
                <Box bg={"#FFF5D9"} w={18} h={18} p={30} mx={4} display="flex" alignItems="center" justifyContent="center" borderRadius="full">
                  <Icon as={FaSackDollar} boxSize={6} color={"#FFBB38"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">Fleet On Duty</Text>
                  <Text fontSize="25px">{globalStats.onDuty || 0}</Text>
                </Flex>
              </Box>
            </GridItem>

            <GridItem key="IdleFleet">
              <Box bg="white" p={4} rounded="md" display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" h="120px" gap={2} borderRadius="3xl">
                <Box bg={"#E7EDFF"} w={18} h={18} p={30} mx={4} display="flex" alignItems="center" justifyContent="center" borderRadius="full">
                  <Icon as={FaHandHoldingUsd} boxSize={6} color={"#396AFF"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">Idle Fleet</Text>
                  <Text fontSize="25px">{globalStats.idle || 0}</Text>
                </Flex>
              </Box>
            </GridItem>

            <GridItem key="MyFleets">
              <Box bg="white" p={4} rounded="md" display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" h="120px" gap={2} borderRadius="3xl">
                <Box bg={"#FFE0EB"} w={18} h={18} p={30} mx={4} display="flex" alignItems="center" justifyContent="center" borderRadius="full">
                  <Icon as={FaFileInvoiceDollar} boxSize={6} color={"#FF82AC"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">My Fleets</Text>
                  <Text fontSize="25px">{globalStats.total || 0}</Text>
                </Flex>
              </Box>
            </GridItem>
          </Grid>
        </Box>

        {/* Search and Filter Bar */}
        <Flex justifyContent={"space-between"} flexDirection={{ base: "column", md: "row" }}>
          <Heading fontSize={"xl"} py={2} color={"#333B69"}>Fleet List</Heading>
          <Flex w="100%" maxW="600px" gap={3} justifyContent={"flex-end"}>

            {/* Search Input */}
            <Flex gap={0} alignItems="center" w="100%" maxW="300px" bg="white" px={3} h="40px" borderRadius={"13"}>
              <FiSearch color="#718EBF" fontSize="1rem" />
              <Input
                placeholder="Search for something"
                border="none"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                _focus={{ outline: "none", boxShadow: "none" }}
                fontSize="sm"
                h="full"
                _placeholder={{ color: "#718EBF" }}
              />
              {searchInput && (
                <IconButton
                  aria-label="Clear search"
                  icon={<Text fontSize="lg">×</Text>}
                  size="sm"
                  variant="ghost"
                  onClick={clearSearch}
                />
              )}
            </Flex>

            {/* Sort Popover */}
            <Popover>
              <PopoverTrigger>
                <Flex gap={2} alignItems="center" justifyContent="center" w="100%" maxW="91px" bg="white" px={3} h="40px" borderRadius={"13"} cursor="pointer">
                  <TbArrowsSort color="#718EBF" fontSize="1rem" />
                  <Text fontSize="sm" color="#718EBF">Sort</Text>
                </Flex>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverBody>
                  <VStack align="stretch" spacing={2}>
                    <Button size="sm" variant="ghost" justifyContent="flex-start" onClick={() => applySort("carNumber")}>
                      Reg No {sortField === "carNumber" && (sortOrder === "asc" ? "↑" : "↓")}
                    </Button>
                    <Button size="sm" variant="ghost" justifyContent="flex-start" onClick={() => applySort("model")}>
                      Model {sortField === "model" && (sortOrder === "asc" ? "↑" : "↓")}
                    </Button>
                    <Button size="sm" variant="ghost" justifyContent="flex-start" onClick={() => applySort("manufacturer")}>
                      Manufacturer {sortField === "manufacturer" && (sortOrder === "asc" ? "↑" : "↓")}
                    </Button>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>

            {/* Filter — custom dropdown */}
            <Box position="relative" ref={filterRef}>
              <Flex
                gap={2}
                alignItems="center"
                justifyContent="center"
                w="91px"
                bg="white"
                px={3}
                h="40px"
                borderRadius={"13"}
                cursor="pointer"
                onClick={handleFilterOpen}
              >
                <ImFilter color="#718EBF" fontSize="1rem" />
                <Text fontSize="sm" color="#718EBF">Filter</Text>
              </Flex>

              {isFilterOpen && (
                <Box
                  position="absolute"
                  right="0"
                  top="48px"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="md"
                  p={4}
                  zIndex={999}
                  minW="220px"
                >
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>Operational Status</Text>
                      <RadioGroup value={tempOperationalStatus} onChange={setTempOperationalStatus}>
                        <VStack align="stretch">
                          <Radio value="">All</Radio>
                          <Radio value="in_review">In Review</Radio>
                          <Radio value="active">Active</Radio>
                          <Radio value="inactive">Inactive</Radio>
                          <Radio value="blacklisted">Blacklisted</Radio>
                        </VStack>
                      </RadioGroup>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>Administrative Status</Text>
                      <RadioGroup value={tempAdministrativeStatus} onChange={setTempAdministrativeStatus}>
                        <VStack align="stretch">
                          <Radio value="">All</Radio>
                          <Radio value="0">Available</Radio>
                          <Radio value="1">On Duty</Radio>
                          <Radio value="2">Under Maintenance</Radio>
                          <Radio value="3">Out Of Service</Radio>
                        </VStack>
                      </RadioGroup>
                    </Box>
                    <Flex gap={2}>
                      <Button size="sm" colorScheme="blue" onClick={applyFilters}>Apply</Button>
                      <Button size="sm" variant="outline" onClick={clearFilters}>Clear</Button>
                    </Flex>
                  </VStack>
                </Box>
              )}
            </Box>

          </Flex>
        </Flex>

        {/* Table */}
        <Box p={7} bg={"white"} overflowX={"auto"} borderRadius={"3xl"}>
          <Table size="sm" bg={"white"} fontSize={"small"}>
            <Thead>
              <Tr>
                <Th color={"#718EBF"}>S.No</Th>
                <Th color={"#718EBF"}>Reg No</Th>
                <Th color={"#718EBF"}>Model</Th>
                <Th color={"#718EBF"}>Manufacturer</Th>
                <Th color={"#718EBF"}>Segment</Th>
                <Th color={"#718EBF"}>Oper. Status</Th>
                <Th color={"#718EBF"}>Admi. Status</Th>
                <Th color={"#718EBF"}>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fetching ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Tr key={i}>
                    <Td colSpan={8}><Skeleton height="32px" borderRadius="md" /></Td>
                  </Tr>
                ))
              ) : sortedData?.length > 0 ? (
                sortedData.map((item, index) => (
                  <Tr key={item?.id} onClick={() => navigate(`/fleet-management/vehicles/${item?.id}`)}>
                    <Td>{(currentPage - 1) * itemsPerPage + index + 1}</Td>
                    <Td>{item?.carNumber}</Td>
                    <Td>{item?.model}</Td>
                    <Td>{item?.manufacturer}</Td>
                    <Td>{item?.category}</Td>
                    <Td>
                      <Badge colorScheme={operationalStatusColorMap?.[item?.operationalStatus]} px={3} py={1} rounded="lg">
                        {operationalStatusMap?.[item?.operationalStatus]}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={administrativeStatusColorMap?.[item?.administrativeStatus]} px={3} py={1} rounded="lg">
                        {administrativeStatusMap?.[item?.administrativeStatus]}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton as={IconButton} icon={<FiMoreVertical />} variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} />
                        <MenuList borderRadius={0}>
                          <MenuItem as={Link} to={`/fleet-management/vehicles/edit/${item.id}`} onClick={(e) => e.stopPropagation()} borderRadius={0}>
                            Edit
                          </MenuItem>
                          <MenuItem onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} borderRadius={0} color="red.500">
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={8}>
                    <Text color="#718EBF">No data found</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          {/* Pagination */}
          <Flex justifyContent="space-between" alignItems="center" mt={4} gap={2}>
            <Flex align="center" gap={4} wrap="wrap" justify="center" mt={4}>
              <Text fontSize="sm" color={"#8B909A"}>Showing:</Text>
              <Select
                size="sm"
                width="fit-content"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {perPageOptions.map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </Select>
              <Text fontSize="sm" color={"#8B909A"}>of {totalFleets}</Text>
            </Flex>

            <Flex alignItems="center" mt={4} gap={2}>
              <Button
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                <GoChevronLeft />
              </Button>

              {totalFleets === 0 ? (
                <Button size="sm" bgColor="#F1F2F6" isDisabled>
                  <Text color="#8B909A">1</Text>
                </Button>
              ) : (
                getPagesToShow().map((i) => (
                  <Button
                    key={i}
                    size="sm"
                    bgColor={i === currentPage ? "#0F60FF" : "#F1F2F6"}
                    onClick={() => setCurrentPage(i)}
                  >
                    <Text color={i === currentPage ? "#FFFFFF" : "#8B909A"}>{i}</Text>
                  </Button>
                ))
              )}

              <Button
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                isDisabled={currentPage === totalPages || totalFleets === 0}
              >
                <GoChevronRight />
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Stack>
    </>
  );
};

export default Fleets;
